/**
 * The two surfaces a version RESTORE needs around it: the confirmation a human
 * sees BEFORE, and the account of what moved AFTER.
 *
 * They live beside `VersionHistoryPanel` rather than inside it because a
 * restore is not a variation on "open a version" — it is the one action in the
 * panel that rewrites a delivered client document, and the honesty of the
 * feature lives entirely in these two components: what the user is told it will
 * cost, and what they are told it actually cost. Keeping them separate keeps
 * both provable in isolation (`VersionHistoryPanel.test.tsx` renders the report
 * card directly against every server-reported shape) and keeps the panel itself
 * inside the file-size limit.
 */

import { useEffect } from "react";
import type { VersionRestoreReport } from "../api";
import { X } from "../compose/Icon";

/**
 * The confirmation a restore requires. It states the CONSEQUENCES rather than
 * asking "are you sure?": a restore rewrites the head of a delivered document,
 * moves the content hash every reviewer verdict is pinned to, and stales any
 * pending agent proposal. It also states the safety net, because the honest
 * reason this is safe to press is that the current head is snapshotted first.
 */
export function RestoreConfirm({
  versionId,
  versionLabel,
  busy,
  onCancel,
  onConfirm,
}: {
  versionId: number;
  /** Human name of the revision (e.g. `"v3"`). Falls back to the raw id — a
   *  reader picking a tick off the timeline knows it as "v3", not as 8817. */
  versionLabel?: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const name = versionLabel ?? `version ${versionId}`;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="ev-restore-confirm"
      data-testid="version-restore-confirm"
      role="alertdialog"
      aria-modal="true"
      aria-label="Revert to this version?"
    >
      <p className="ev-restore-confirm__lead">
        Revert to {name}?
      </p>
      <ul className="ev-restore-confirm__effects">
        {/* THE FIRST THING IT SAYS IS THAT NOTHING IS LOST. This dialog used to
            open on "the content is replaced", which reads as destruction and is
            the reason a reader hesitates over a button that is, in fact, purely
            additive. The operation is `git revert`, not `git reset`: a NEW
            revision is appended carrying that version's content, and every
            revision in between stays in the history under its own number. */}
        <li>
          A <strong>new version</strong> is created holding {name}'s content.
          Nothing is deleted — every version since {name} stays in the history,
          and the new one is labelled as a revert of {name}.
        </li>
        <li>The document's content becomes that version's exact bytes.</li>
        <li>
          Reviewer verdicts pinned to the current content stop counting, so the
          delivery gate may close.
        </li>
        <li>Pending agent proposals become stale and need re-basing.</li>
        <li>
          The current content is snapshotted first, so this revert can itself be
          undone.
        </li>
      </ul>
      <div className="ev-restore-confirm__actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          data-testid="version-restore-cancel"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn--sm"
          data-testid="version-restore-confirm-go"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? "Reverting…" : `Revert to ${name}`}
        </button>
      </div>
    </div>
  );
}

/**
 * What the restore actually MOVED, straight from the server's report. This is
 * deliberately not a "Done ✓" toast: the terminal answers with measurements
 * (bytes, hashes, lost verdicts, staled proposals) and hiding them would put
 * the UI back in the position of contradicting the server.
 */
export function RestoreReportCard({
  report,
  onDismiss,
}: {
  report: VersionRestoreReport;
  onDismiss: () => void;
}) {
  return (
    <div className="ev-restore-report" data-testid="version-restore-report">
      <div className="ev-restore-report__head">
        <strong>
          {report.bytes_written
            ? `Reverted to version ${report.restored_from_version_id}`
            : `Version ${report.restored_from_version_id} was already the stored content`}
        </strong>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          data-testid="version-restore-report-dismiss"
          aria-label="Dismiss the restore report"
          onClick={onDismiss}
        >
          <X size={14} strokeWidth={1.5} />
        </button>
      </div>
      <dl className="ev-restore-report__facts">
        <dt>Content size</dt>
        <dd data-testid="restore-bytes">
          {report.bytes_before} → {report.bytes_after} bytes
        </dd>
        <dt>Content hash</dt>
        <dd data-testid="restore-hash">
          {report.content_hash_changed
            ? `${report.content_hash_before.slice(0, 12)} → ${report.content_hash_after.slice(0, 12)}`
            : "unchanged"}
        </dd>
        <dt>New version</dt>
        <dd data-testid="restore-version-id">{report.doc_version_id}</dd>
        {report.backup_version_id > 0 ? (
          <>
            <dt>Undo point</dt>
            <dd data-testid="restore-backup-id">
              version {report.backup_version_id}
            </dd>
          </>
        ) : null}
        {report.verdicts_invalidated.length > 0 ? (
          <>
            <dt>Verdicts no longer counting</dt>
            <dd data-testid="restore-verdicts-lost">
              {report.verdicts_invalidated.join(", ")}
            </dd>
          </>
        ) : null}
        {report.verdicts_carried.length > 0 ? (
          <>
            <dt>Verdicts still counting</dt>
            <dd data-testid="restore-verdicts-kept">
              {report.verdicts_carried.join(", ")}
            </dd>
          </>
        ) : null}
        {report.proposals_staled > 0 ? (
          <>
            <dt>Proposals staled</dt>
            <dd data-testid="restore-proposals-staled">
              {report.proposals_staled}
            </dd>
          </>
        ) : null}
      </dl>
      {report.warning ? (
        <p className="ev-restore-report__warning" data-testid="restore-warning">
          {report.warning}
        </p>
      ) : null}
    </div>
  );
}

